import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchOrders } from '../reducers/positionSlice';
import { ClosedPositionsPage } from '../routes/closed-positions-page';
import { getStartDate } from '../utils/utils';

interface ClosedPosition {
    symbol: string;
    quantity: number;
    avgBuyPrice: number;
    avgSellPrice: number;
    totalBuyValue: number;
    totalSellValue: number;
    profitLoss: number;
    profitLossPercent: number;
    trades: number;
    closedDate: string | null;
}

export const ClosedPositionsPageContainer: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { orders } = useSelector((state: RootState) => state.alpaca);
    const [period, setPeriod] = React.useState('1Y');
    const [ordersAfterDate, setOrdersAfterDate] = React.useState(getStartDate('1Y', new Date()));

    React.useEffect(() => {
        dispatch(fetchOrders(ordersAfterDate));
    }, [dispatch, ordersAfterDate]);

    const updateClosedPositionsPeriod = (nextPeriod: string) => {
        const nextDate = nextPeriod === 'all' ? '1970-01-01T00:00:00Z' : getStartDate(nextPeriod, new Date());
        setPeriod(nextPeriod);
        setOrdersAfterDate(nextDate);
    };

    const calculateClosedPositions = (): ClosedPosition[] => {
        const closedPositionsMap: { [key: string]: ClosedPosition } = {};
        const tradeHistory: { [key: string]: any[] } = {}; // Track buy prices per stock symbol
        
        // Sort orders by transaction_time in ascending order (oldest first)
        const sortedOrders = [...orders].sort((a, b) => 
            new Date(a.transaction_time).getTime() - new Date(b.transaction_time).getTime()
        );

        for (const order of sortedOrders) {
            const { symbol, price: filled_avg_price, qty, side, transaction_time: date } = order;
            const quantity = parseFloat(qty);
            const price = parseFloat(filled_avg_price);

            if (!tradeHistory[symbol]) {
                tradeHistory[symbol] = [];
                closedPositionsMap[symbol] = {
                    symbol,
                    quantity: 0,
                    avgBuyPrice: 0,
                    avgSellPrice: 0,
                    totalBuyValue: 0,
                    totalSellValue: 0,
                    profitLoss: 0,
                    profitLossPercent: 0,
                    trades: 0,
                    closedDate: null,
                };
            }

            if (side === 'buy') {
                // Store buy order details
                tradeHistory[symbol].push({ price, quantity, side, date, available_qty: quantity });
                closedPositionsMap[symbol].totalBuyValue += price * quantity;
                closedPositionsMap[symbol].quantity += quantity;
                closedPositionsMap[symbol].trades += 1;
            } else if (side === 'sell') {
                let remainingQty = quantity;
                const ordersToRestore = [];
                let totalSellProfitLoss = 0;
                let totalSellQuantity = 0;
                
                while (remainingQty > 0 && tradeHistory[symbol].length > 0) {
                    const buyOrder = tradeHistory[symbol].shift();
                    if (buyOrder.side === 'buy' && buyOrder.available_qty > 0) {
                        const qtyToSell = Math.min(remainingQty, buyOrder.available_qty);

                        // Profit = (Sell Price - Buy Price) * Quantity
                        const tradePnL = (price - buyOrder.price) * qtyToSell;
                        totalSellProfitLoss += tradePnL;
                        totalSellQuantity += qtyToSell;

                        remainingQty -= qtyToSell;
                        buyOrder.available_qty -= qtyToSell;
                    }

                    ordersToRestore.unshift(buyOrder);
                }

                if (remainingQty === 0 || tradeHistory[symbol].length === 0) {
                    tradeHistory[symbol].unshift(...ordersToRestore);
                }

                // Update closed position
                closedPositionsMap[symbol].totalSellValue += price * totalSellQuantity;
                closedPositionsMap[symbol].profitLoss += totalSellProfitLoss;
                closedPositionsMap[symbol].trades += 1;
                if (!closedPositionsMap[symbol].closedDate || new Date(date).getTime() > new Date(closedPositionsMap[symbol].closedDate as string).getTime()) {
                    closedPositionsMap[symbol].closedDate = date;
                }
            }
        }

        // Calculate remaining metrics for closed positions
        Object.keys(closedPositionsMap).forEach(symbol => {
            const pos = closedPositionsMap[symbol];
            if (pos.totalBuyValue > 0) {
                pos.avgBuyPrice = pos.totalBuyValue / pos.quantity;
            }
            if (pos.totalSellValue > 0 && pos.quantity > 0) {
                pos.avgSellPrice = pos.totalSellValue / pos.quantity;
                pos.profitLossPercent = (pos.profitLoss / pos.totalBuyValue) * 100;
            }
        });

        // Filter positions with closed trades (profitLoss !== 0) and sort by profitLoss descending
        return Object.values(closedPositionsMap)
            .filter(pos => pos.profitLoss !== 0)
            .sort((a, b) => b.profitLoss - a.profitLoss);
    };

    const closedPositions = calculateClosedPositions();

    return (
        <ClosedPositionsPage
            closedPositions={closedPositions}
            chartPeriod={period}
            updatePeriod={updateClosedPositionsPeriod}
        />
    );
};
